import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import Icon from '../components/ui/Icon.jsx';
import VerifyEmailBanner from '../components/ui/VerifyEmailBanner.jsx';
import { useApi } from '../hooks/useApi.js';
import { useToast } from '../context/ToastContext.jsx';
import { getVirtualProducts } from '../api/virtualApi.js';
import { submitInquiry } from '../api/inquiriesApi.js';
import { getSavedCards } from '../api/savedCardsApi.js';
import { getPoints } from '../api/accountApi.js';
import { PAY_METHODS } from '../data/account.js';
import { LINE_ID, LINE_ADD_URL } from '../constants/contact.js';
import { analyticsEvent, gaItem } from '../utils/analytics.js';
import { getDefaultSavedCard } from '../utils/savedCards.js';
import './RegisterPage.css';

// 取得商品的變體（種類/角色）；沒設 variants 的商品 fallback 成單一「標準」
const variantsOf = (p) =>
  p?.variants?.length ? p.variants : (p ? [{ id: 'default', name: '標準', price: p.price ?? 0 }] : []);

let _uid = 0;
const makeEntry = (variants) => {
  const v0 = variants[0] || { id: '', price: 0 };
  return { uid: ++_uid, variantId: v0.id, savedCardId: null,
    cardName: '', account: '', note: '', amount: v0.price ?? 0 };
};

const TRACK = ['已送出', '處理中', '已完成'];

function RegisterPage() {
  const [params] = useSearchParams();
  const presetItem = params.get('item');
  const presetVariant = params.get('variant'); // 詳情頁先選好的角色
  const navigate = useNavigate();
  // 一次拉足夠多筆供挑選（虛擬商品數量少）
  const { data, loading } = useApi(() => getVirtualProducts({ limit: 100 }), []);
  const products = useMemo(() => data?.items ?? [], [data]);
  // 會員的常用卡片（代打資料存的），供「一鍵帶入」
  const { data: savedCards = [], loading: cardsLoading } = useApi(() => getSavedCards(), []);

  const { showToast } = useToast();
  const [entries, setEntries] = useState([]);
  const [pay, setPay] = useState('transfer');
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pointsBalance, setPointsBalance] = useState(0);
  const [pointsInput, setPointsInput] = useState(0); // 使用者想折抵的點數

  // 固定商品：登記頁綁定「剛剛點進來的那個虛擬商品」，不可更換（用 slug 比對）
  const product = useMemo(
    () => presetItem
      ? products.find(p => String(p.slug ?? p.id) === String(presetItem)) || null
      : products[0] || null,
    [products, presetItem]
  );
  const variants = useMemo(() => variantsOf(product), [product]);
  const variantById = useMemo(() => Object.fromEntries(variants.map(v => [v.id, v])), [variants]);

  // 商品 + 常用卡片都載入後種第一列（帶入詳情頁先選好的角色 + 預設卡片）。
  // 等 cardsLoading 結束才種，否則卡片還沒到會種成空的、之後也不會補。
  useEffect(() => {
    if (!product || entries.length || cardsLoading) return;
    const first = makeEntry(variants);
    if (presetVariant && variantById[presetVariant]) {
      first.variantId = presetVariant;
      first.amount = variantById[presetVariant].price;
    }
    // 預設卡片優先；沒設預設就退而使用 ID 最小的第一張（防呆）
    const def = getDefaultSavedCard(savedCards);
    if (def) {
      first.savedCardId = def.id;
      first.cardName = def.cardName || '';
      first.account = def.account || '';
      first.note = def.note || '';
    }
    setEntries([first]);
  }, [product, variants, variantById, presetVariant, entries.length, savedCards, cardsLoading]);

  // 載入會員點數餘額（供登記折抵）
  useEffect(() => {
    getPoints().then(p => setPointsBalance(p?.balance ?? 0)).catch(() => {});
  }, []);

  const total = entries.reduce((sum, e) => sum + Math.floor(Number(e.amount) || 0), 0);
  // 折抵上限 = min(餘額, 總額)；實際折抵夾在 0~上限，實付 = 總額 − 折抵
  const maxRedeem = Math.min(pointsBalance, total);
  // 點數是整數，輸入取整（避免小數折抵：前端顯示小數、後端 Integer 截斷會對不上）
  const redeem = Math.max(0, Math.min(Math.floor(Number(pointsInput) || 0), maxRedeem));
  const payable = total - redeem;

  const patch = (uid, key, val) =>
    setEntries(prev => prev.map(e => {
      if (e.uid !== uid) return e;
      if (key === 'cardName' || key === 'account') {
        return { ...e, [key]: val, savedCardId: null };
      }
      return { ...e, [key]: val };
    }));

  const addRow = () => setEntries(prev => [...prev, makeEntry(variants)]);

  // 從常用卡片（代打資料）快速加一列，帶入卡名 / 帳號 / 備註
  const addFromCard = (sc) =>
    setEntries(prev => [
      ...prev,
      { ...makeEntry(variants), savedCardId: sc.id, cardName: sc.cardName,
        account: sc.account || '', note: sc.note || '' },
    ]);

  const delRow = (uid) =>
    setEntries(prev => (prev.length > 1 ? prev.filter(e => e.uid !== uid) : prev));

  // 選角色：同時把金額帶成該角色對應價（使用者仍可手動改）
  const onSelectVariant = (uid, variantId) => {
    setEntries(prev =>
      prev.map(e =>
        e.uid === uid ? { ...e, variantId, amount: variantById[variantId]?.price ?? e.amount } : e
      )
    );
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        productId: String(product.slug ?? product.id),
        productName: product.name,
        payMethod: pay,
        items: entries.map(e => ({
          savedCardId: e.savedCardId,
          variantName: variantById[e.variantId]?.name || '',
          cardName: e.cardName,
          account: e.account,
          note: e.note,
          amount: Math.floor(Number(e.amount) || 0),
        })),
        pointsToRedeem: redeem > 0 ? redeem : undefined,
      };
      const res = await submitInquiry(payload);
      const analyticsItems = entries.map((entry, index) => gaItem({
        id: entry.variantId,
        name: product.name,
        variantName: variantById[entry.variantId]?.name,
        amount: Math.floor(Number(entry.amount) || 0),
        quantity: 1,
        category: 'virtual_inquiry',
      }, index));
      const inquiryEvent = {
        currency: 'TWD',
        value: Number(res.estimatedTotal ?? total),
        lead_source: 'virtual_product',
        product_id: String(product.slug ?? product.id),
        items: analyticsItems,
      };
      analyticsEvent('generate_lead', inquiryEvent);
      analyticsEvent('submit_inquiry', { ...inquiryEvent, inquiry_item_count: entries.length });
      setSubmitted({
        refNo: res.refNo,
        count: res.items?.length ?? entries.length,
        total: res.estimatedTotal ?? total,
        redeemed: res.pointsRedeemed ?? redeem,
        pay,
      });
      window.scrollTo({ top: 0, behavior: 'auto' });
    } catch (err) {
      showToast(`送出失敗：${err.message}`, 'error', 3000);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    const payLabel = PAY_METHODS.find(m => m.id === submitted.pay)?.label;
    return (
      <main className="reg-page">
        <div className="reg-done">
          <div className="reg-done-ic"><Icon name="check" size={40} /></div>
          <h2>登記已送出！</h2>
          <p>
            我們已收到你的 {submitted.count} 筆登記。團隊會盡快確認，並以 <b>{payLabel}</b> 通知你應付金額，
            確認前不會收款。你可隨時在會員中心追蹤進度。
          </p>
          <div className="reg-ticket">
            <div className="reg-ticket-top">
              <div><small>登記編號</small><br /><b>{submitted.refNo || '—'}</b></div>
              <div style={{ textAlign: 'right' }}>
                <small>{submitted.redeemed > 0 ? '折抵後應付' : '預估總額'}</small><br />
                <b style={{ color: 'var(--red)' }}>NT${(submitted.total - (submitted.redeemed || 0)).toLocaleString()}</b>
                {submitted.redeemed > 0 && (
                  <div style={{ font: '600 11px Inter', color: 'var(--ink-soft)' }}>已折抵 {submitted.redeemed.toLocaleString()} P</div>
                )}
              </div>
            </div>
            <div className="reg-track">
              {TRACK.map((t, i) => (
                <div key={t} className={`reg-tk ${i === 0 ? 'on' : ''}`}>
                  <div className="d">{i === 0 ? <Icon name="check" size={12} /> : i + 1}</div>
                  <small>{t}</small>
                </div>
              ))}
            </div>
          </div>
          <div className="reg-done-actions">
            <button className="btn btn-primary" onClick={() => navigate('/account?tab=boost')}>
              <Icon name="list" size={16} /> 查看我的登記
            </button>
            <button className="btn btn-line" onClick={() => navigate('/virtual')}>繼續逛虛擬商品</button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="reg-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{ background: 'none', border: 'none', padding: 0, margin: '0 0 12px', font: '700 13px "Noto Sans TC", sans-serif', color: 'var(--steel)', cursor: 'pointer' }}
      >← 上一頁</button>
      <nav className="reg-crumbs">
        <Link to="/virtual">虛擬商品</Link> / <b>登記報名</b>
      </nav>
      <div className="reg-head">
        <p className="reg-eyebrow">REGISTRATION · 站內登記</p>
        <h1 className="reg-title">虛擬商品 · 登記報名</h1>
        <p className="reg-sub">
          先送出登記，我們確認後再通知付款。同一個商品可一次幫多位朋友登記（下方一列一張卡）。
        </p>
      </div>

      <VerifyEmailBanner />

      {/* 初次購買 LINE 提示 */}
      <div className="line-note">
        <span className="line-note-ic"><Icon name="mail" size={20} /></span>
        <p>
          <b>初次購買的朋友請先加官方 LINE。</b>第一次登記時，請加入官方 LINE（ID：{LINE_ID}）由專人協助核對 / 提供登入資料；
          老客戶我們已留存資料，直接填下方即可。為保護帳號安全，<b>網站上不需填寫密碼</b>。
        </p>
        <button type="button" className="line-btn" onClick={() => window.open(LINE_ADD_URL, '_blank', 'noopener,noreferrer')}>加官方 LINE</button>
      </div>

      {loading ? (
        <div className="reg-loading">載入中…</div>
      ) : !product ? (
        <div className="reg-loading">找不到商品，請回 <Link to="/virtual">虛擬商品</Link> 重新選擇。</div>
      ) : (
        <>
          {/* 固定商品橫幅：登記頁綁定此商品，不可更換 */}
          <div className="admin-card" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', marginBottom: 16 }}>
            {(product.images?.[0] || product.thumbUrl) && (
              <img src={product.images?.[0] || product.thumbUrl} alt=""
                style={{ width: 56, height: 35, objectFit: 'cover', borderRadius: 6 }} />
            )}
            <div>
              <div style={{ font: '800 16px "Noto Sans TC"', color: 'var(--navy)' }}>{product.name}</div>
            </div>
          </div>

          <div className="sheet-wrap">
            <div className="sheet-toolbar">
              <span className="sheet-tlabel"><Icon name="list" size={18} /> 批次登記單 · 一列一張卡</span>
              {savedCards.length > 0 ? (
                <>
                  <span className="sheet-quick-hint">從常用卡片快速加列：</span>
                  {savedCards.map(sc => (
                    <button key={sc.id} type="button" className="sheet-quick" onClick={() => addFromCard(sc)}>
                      <Icon name="plus" size={13} /> {sc.cardName}
                    </button>
                  ))}
                </>
              ) : !cardsLoading && (
                <>
                  <span className="sheet-quick-hint">還沒有常用卡片？設定後下次登記可一鍵帶入：</span>
                  <Link to="/account?tab=boost" className="sheet-quick" style={{ textDecoration: 'none' }}>
                    <Icon name="plus" size={13} /> 去設定常用卡片
                  </Link>
                </>
              )}
            </div>

            <div className="sheet-scroll">
              <table className="sheet">
                <thead>
                  <tr>
                    <th className="rownum">#</th>
                    <th>種類 / 角色 <span className="req">*</span></th>
                    <th>卡名 <span className="req">*</span></th>
                    <th>帳號（信箱）<span className="req">*</span></th>
                    <th>備註</th>
                    <th style={{ textAlign: 'right' }}>金額</th>
                    <th aria-label="操作" />
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr key={e.uid}>
                      <td className="rownum">{i + 1}</td>
                      <td className="cell-variant" data-label="種類 / 角色" style={{ minWidth: 150 }}>
                        <select value={e.variantId} onChange={ev => onSelectVariant(e.uid, ev.target.value)}>
                          {variants.map(v => <option key={v.id} value={v.id}>{v.name}（NT${v.price}）</option>)}
                        </select>
                      </td>
                      <td data-label="卡名" style={{ minWidth: 140 }}>
                        <input value={e.cardName} onChange={ev => patch(e.uid, 'cardName', ev.target.value)} placeholder="卡片名稱 / 暱稱" />
                      </td>
                      <td data-label="帳號（信箱）" style={{ minWidth: 160 }}>
                        <input value={e.account} onChange={ev => patch(e.uid, 'account', ev.target.value)} placeholder="example@mail.com" />
                      </td>
                      <td data-label="備註" style={{ minWidth: 120 }}>
                        <input value={e.note} onChange={ev => patch(e.uid, 'note', ev.target.value)} placeholder="備註（選填）" />
                      </td>
                      <td className="amount" data-label="金額" style={{ minWidth: 90 }}>
                        <input type="number" min="0" step="50" value={e.amount} onChange={ev => patch(e.uid, 'amount', ev.target.value)} />
                      </td>
                      <td className="del-cell">
                        <button type="button" className="row-del" onClick={() => delRow(e.uid)} aria-label="刪除此列">
                          <Icon name="trash" size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="sheet-add">
              <button type="button" className="sheet-addbtn" onClick={addRow}>
                <Icon name="plus" size={16} /> 新增一列
              </button>
              <span className="sheet-count">目前 {entries.length} 列 · 一列代表一張卡 / 一位朋友</span>
            </div>

            <div className="sheet-foot">
              <div className="sheet-pay">
                <span className="pl">付款方式：</span>
                {PAY_METHODS.map(m => (
                  <button
                    key={m.id}
                    type="button"
                    className={`sheet-pay-pill ${pay === m.id ? 'on' : ''}`}
                    onClick={() => setPay(m.id)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {pointsBalance > 0 && (
                <div className="sheet-points">
                  <span className="sheet-points-label">
                    紅利折抵 <small>可用 {pointsBalance.toLocaleString()} P</small>
                  </span>
                  <div className="sheet-points-input">
                    <input type="number" min="0" max={maxRedeem} step="1" value={pointsInput}
                      onChange={e => setPointsInput(e.target.value)} placeholder="0" aria-label="使用點數" />
                    <button type="button" onClick={() => setPointsInput(maxRedeem)}>用好用滿</button>
                  </div>
                  {redeem > 0 && (
                    <span className="sheet-points-cut">−NT${redeem.toLocaleString()}</span>
                  )}
                </div>
              )}
              <div className="sheet-total">
                <div className="tt">
                  <small>{redeem > 0 ? `折抵後應付 · 折 ${redeem.toLocaleString()}P` : `預估總額 · ${entries.length} 筆`}</small>
                  <b>NT${payable.toLocaleString()}</b>
                </div>
                <button type="button" className="btn btn-gold" onClick={submit} disabled={!entries.length || submitting}>
                  <Icon name="check" size={18} /> {submitting ? '送出中…' : '送出登記'}
                </button>
              </div>
            </div>
          </div>

          <div className="sec-note">
            <span className="sec-note-ic"><Icon name="shield" size={20} /></span>
            <p>
              <b>先送出登記，我們確認後再通知付款，現在不收款。</b>
              金額為預估，實際以確認後通知為準。登入密碼不在網站填寫，由官方 LINE 私下提供 / 我們已留存；
              資料僅用於本次綁定作業，完成後刪除。
            </p>
          </div>
        </>
      )}
    </main>
  );
}

export default RegisterPage;
